import { useEffect, useState, type ReactNode } from 'react';
import { AppLoadingSplash } from '@eureca/ui';
import {
  setAccessTokenProvider,
  setForceTokenRefresh,
  setUnauthorizedHandler,
} from '@eureca/candidate-api-sdk/client';
import { SessionExpiredScreen } from '@/shared/error-screens';
import { bootIntercom } from '@/intercom';
import { bootPosthog } from '@/posthog';
import { peekJustSignedUp } from '@/features/account/onboarding/shared/signup-welcome-flag';
import { maybeSendTrafficOrigin } from '@/traffic-origin';
import { fireAuthDataLayerEvent } from './datalayer-auth';
import { ensureKeycloakInitialized, getKeycloak } from './keycloak-instance';
import { startProactiveTokenRefresh } from './proactive-token-refresh';
import { publishSessionInfo } from './session-info';

export function KeycloakGate({ children }: { children: ReactNode }) {
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sessionExpired, setSessionExpired] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let stopProactiveRefresh: (() => void) | null = null;

    (async () => {
      try {
        const authenticated = await ensureKeycloakInitialized();
        if (cancelled) return;

        // Boot the Intercom messenger once auth state is known — identified when
        // logged in, anonymous otherwise (replica do `auth-guard` do candidate-web).
        bootIntercom();

        // Boot PostHog (product analytics + session replay, EU-331) once auth
        // state is known — identified when logged in, anonymous otherwise. Same
        // build serves both coexistência v1↔v2 and V2, so this covers both fronts.
        bootPosthog();

        // EU-395 — `sign_up` (1ª criação de conta) ou `login` (existente) no
        // retorno do SSO, conforme o spec (dispara ao voltar ao app logado).
        // `peekJustSignedUp()` decide qual. Token-only user_data; deduplicado por
        // sub+auth_time. Não-fatal: falha de analytics nunca bloqueia o boot.
        if (authenticated) {
          try {
            fireAuthDataLayerEvent(
              getKeycloak().tokenParsed as Record<string, unknown> | undefined,
              peekJustSignedUp()
            );
          } catch (e) {
            console.error('[eureca-datalayer] auth event failed (non-fatal)', e);
          }
        }

        if (!authenticated) {
          setAccessTokenProvider(null);
          setForceTokenRefresh(null);
          setReady(true);
          return;
        }

        const keycloak = getKeycloak();

        // DEV-555 — espelho não sensível da sessão (instantes, nunca tokens) em
        // window.__eurecaSession: é o que mostra que a sessão vale 30 dias a cada
        // uso e que a confirmação de identidade não mexe nela.
        publishSessionInfo(keycloak);
        keycloak.onAuthRefreshSuccess = () => publishSessionInfo(keycloak);

        setAccessTokenProvider(async () => {
          try {
            // Refresh if token expires within 60s (was 30s) — wider buffer to
            // avoid race where token expires between provider call and BFF receipt.
            await keycloak.updateToken(60);
            return keycloak.token ?? undefined;
          } catch (err) {
            console.error('[KeycloakGate] updateToken failed — clearing session', err);
            // Do NOT call keycloak.login() here. The unauthorized handler will
            // decide what to do on 401. Calling login() inside the access-token
            // provider creates a redirect-loop when KC has a session but the
            // refresh token is invalid (re-issues a new code, but the SPA's
            // state is stale → loops).
            return undefined;
          }
        });

        // Recuperação de 401 (EU-665): quando o BFF devolve 401 (access token
        // vencido ou refresh transitório enquanto um formulário SurveyJS longo
        // fica aberto), o transporte força um refresh e repete a request UMA
        // vez com o bearer novo, ANTES de tratar a sessão como expirada.
        setForceTokenRefresh(async () => {
          try {
            await keycloak.updateToken(-1);
            return keycloak.token ?? undefined;
          } catch (err) {
            console.error('[KeycloakGate] forced updateToken failed', err);
            return undefined;
          }
        });

        // Ruído de 401 (~56k/dia): o access token vive 300s e os caminhos acima
        // são REATIVOS (por request + no 401), então a cada ~5 min o token vence
        // e uma leva de requests racea no boundary → rajada de 401. Um timer que
        // renova ANTES de vencer mantém o token quente e elimina a rajada
        // (TTL/segurança intactos). Limpo no cleanup do effect.
        stopProactiveRefresh = startProactiveTokenRefresh(keycloak);

        setUnauthorizedHandler(() => {
          // EU-636 (UNI-019) — 401: token inválido ou refresh falhou. Limpa o
          // estado local do KC e mostra a tela "Sessão expirada" (substitui o
          // redirect silencioso p/ /oportunidades). O candidato clica "Entrar"
          // p/ re-autenticar do zero — evita o loop de re-emissão silenciosa de
          // código com o estado da SPA quebrado.
          console.error(
            '[KeycloakGate] BFF returned 401 — clearing token, showing session-expired screen'
          );
          try {
            keycloak.clearToken();
          } catch {
            /* ignore */
          }
          setAccessTokenProvider(null);
          setSessionExpired(true);
        });

        // EU-588 item 2 — envia a origem de tráfego first-touch (1×/sessão
        // POR USUÁRIO — o sub escopa as flags; sem ele, o 2º usuário do mesmo
        // browser nunca enviava), fire-and-forget, DEPOIS de armar o
        // access-token provider (o BFF exige bearer). Nunca quebra o boot.
        void maybeSendTrafficOrigin(keycloak.tokenParsed?.sub);

        setReady(true);
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Keycloak init failed');
        }
      }
    })();

    return () => {
      cancelled = true;
      stopProactiveRefresh?.();
      setAccessTokenProvider(null);
      setForceTokenRefresh(null);
      setUnauthorizedHandler(null);
    };
  }, []);

  if (error) {
    return (
      <div style={{ padding: '2rem', fontFamily: 'system-ui', maxWidth: '28rem' }}>
        <p>Authentication error: {error}</p>
      </div>
    );
  }

  if (!ready) {
    // Brand splash while Keycloak initialises — the shared global loading
    // skeleton (pulsing Eureca mark). `login-loading` testid kept for the e2e
    // login flow (scripts/e2e/stg/candidate/login.ts).
    return <AppLoadingSplash label="Entrando" data-testid="login-loading" />;
  }

  // EU-636 (UNI-019) — sessão expirada (401 do BFF). O candidato re-autentica
  // clicando "Entrar" (fresh login), sem loop de redirect.
  if (sessionExpired) {
    return <SessionExpiredScreen onLogin={() => getKeycloak().login()} />;
  }

  return <>{children}</>;
}
